import React, { useState, useEffect, useCallback } from "react";
import "./admin_clarity.css";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import {
  ArrowLeft,
  ChevronRight,
  Copy,
  Download,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  History,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: "bg-muted text-muted-foreground",
  internal_review: "bg-amber-500/15 text-amber-600 dark:text-[#007a9e] dark:text-amber-400",
  sent_to_client: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  approved: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  cancelled: "bg-red-500/15 text-red-600 dark:text-red-400",
  superseded: "bg-muted text-muted-foreground line-through",
};

const STATUS_LABELS = {
  draft: "Draft",
  internal_review: "Internal Review",
  sent_to_client: "Sent to Client",
  approved: "Approved",
  cancelled: "Cancelled",
  superseded: "Superseded",
};

// ── SOW List View ───────────────────────────────────────────

function SOWList({ sows, onSelect, onCreate, onRefresh, loading }) {
  return (
    <div data-testid="sow-list">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-[var(--brand)]" />
            <h1 className="text-2xl font-semibold text-foreground">
              Scope of Services
            </h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Create, manage, and track SOWs for customer engagements.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} className="gap-2">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2 bg-[var(--brand)] text-white hover:bg-[var(--brand)]/90" data-testid="create-sow-btn">
                <Plus className="h-4 w-4" /> New SOW
              </Button>
            </DialogTrigger>
            <DialogContent className="border-border bg-card text-foreground">
              <DialogHeader>
                <DialogTitle>Create Scope of Services</DialogTitle>
              </DialogHeader>
              <CreateSOWForm onCreate={onCreate} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : sows.length === 0 ? (
        <Card className="border-border bg-card p-12 text-center">
          <FileText className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <p className="mt-3 text-muted-foreground">No SOWs created yet</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {sows.map((sow) => (
            <Card
              key={sow.id}
              className="cursor-pointer border-border bg-card p-4 transition-colors hover:bg-muted/50"
              onClick={() => onSelect(sow)}
              data-testid={`sow-row-${sow.sow_number}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-foreground">
                        {sow.sow_number}
                      </span>
                      <Badge className={`text-xs ${STATUS_STYLES[sow.status]}`}>
                        {STATUS_LABELS[sow.status]}
                      </Badge>
                      <span className="text-xs text-muted-foreground">v{sow.version}</span>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">
                      {sow.client_legal_name || "—"} &middot; {sow.phase_name}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-foreground">
                    ${(sow.base_fee + (sow.additional_workflow_count || 0) * (sow.additional_workflow_unit_price || 0)).toLocaleString()}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Create SOW Form ─────────────────────────────────────────

function CreateSOWForm({ onCreate }) {
  const [form, setForm] = useState({
    tenant_id: "",
    client_legal_name: "",
    client_primary_contact: "",
    client_email: "",
  });
  const [tenants, setTenants] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/tenants/provisioned`, {
      headers: { "x-tenant-id": "tenant_1", "x-user-id": "admin", "x-user-role": "admin" },
    }).then(r => r.json()).then(setTenants).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!form.tenant_id && !form.client_legal_name) {
      toast.error("Provide at least a tenant or client name");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/sow/admin/instances`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`SOW ${data.sow_number} created`);
      onCreate(data);
    } catch {
      toast.error("Failed to create SOW");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4 pt-2">
      {tenants.length > 0 && (
        <div>
          <Label className="text-xs text-muted-foreground">Tenant</Label>
          <Select value={form.tenant_id} onValueChange={(v) => {
            const tenant = tenants.find(t => t.id === v);
            setForm({
              ...form,
              tenant_id: v,
              client_legal_name: form.client_legal_name || tenant?.name || "",
              client_primary_contact: form.client_primary_contact || tenant?.contact_name || "",
              client_email: form.client_email || tenant?.contact_email || "",
            });
          }}>
            <SelectTrigger className="mt-1 border-border bg-background text-foreground">
              <SelectValue placeholder="Select tenant" />
            </SelectTrigger>
            <SelectContent className="border-border bg-popover text-popover-foreground">
              {tenants.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name || t.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label className="text-xs text-muted-foreground">Tenant ID (or enter manually)</Label>
        <Input value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} className="mt-1 border-border bg-background text-foreground" placeholder="tenant_xxx" />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Client Legal Name</Label>
        <Input value={form.client_legal_name} onChange={(e) => setForm({ ...form, client_legal_name: e.target.value })} className="mt-1 border-border bg-background text-foreground" />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Primary Contact</Label>
        <Input value={form.client_primary_contact} onChange={(e) => setForm({ ...form, client_primary_contact: e.target.value })} className="mt-1 border-border bg-background text-foreground" />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Client Email</Label>
        <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="mt-1 border-border bg-background text-foreground" />
      </div>
      <Button onClick={handleCreate} disabled={creating} className="w-full gap-2 bg-[var(--brand)] text-white">
        {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        Create SOW
      </Button>
    </div>
  );
}

// ── SOW Detail View ─────────────────────────────────────────

function SOWDetail({ sowId, onBack }) {
  const [sow, setSow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [actionLoading, setActionLoading] = useState("");

  const fetchSOW = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sow/admin/instances/${sowId}`);
      const data = await res.json();
      setSow(data);
    } catch {
      toast.error("Failed to load SOW");
    } finally {
      setLoading(false);
    }
  }, [sowId]);

  useEffect(() => { fetchSOW(); }, [fetchSOW]);

  const fetchAudit = async () => {
    const res = await fetch(`${API}/api/sow/admin/instances/${sowId}/audit`);
    setAudit(await res.json());
    setShowAudit(true);
  };

  const doAction = async (action, label) => {
    setActionLoading(action);
    try {
      const res = await fetch(`${API}/api/sow/admin/instances/${sowId}/${action}`, { method: "POST" });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Failed"); }
      toast.success(label);
      fetchSOW();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionLoading("");
    }
  };

  const updateField = async (field, value) => {
    await fetch(`${API}/api/sow/admin/instances/${sowId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [field]: value }),
    });
    fetchSOW();
  };

  const updateSection = async (key, value) => {
    const sections = { ...sow.short_form_sections, [key]: value };
    await fetch(`${API}/api/sow/admin/instances/${sowId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ short_form_sections: sections }),
    });
    fetchSOW();
  };

  const downloadPDF = () => {
    window.open(`${API}/api/sow/admin/instances/${sowId}/pdf`, "_blank");
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!sow) return <p className="text-muted-foreground">SOW not found</p>;

  const isLocked = sow.status === "approved" || sow.status === "superseded";
  const total = sow.base_fee + (sow.additional_workflow_count || 0) * (sow.additional_workflow_unit_price || 0);

  return (
    <div data-testid="sow-detail">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-lg font-semibold text-foreground">{sow.sow_number}</span>
              <Badge className={`text-xs ${STATUS_STYLES[sow.status]}`}>{STATUS_LABELS[sow.status]}</Badge>
              <span className="text-xs text-muted-foreground">v{sow.version}</span>
            </div>
            <p className="text-sm text-muted-foreground">{sow.client_legal_name} &middot; {sow.phase_name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAudit} className="gap-1"><History className="h-3 w-3" /> Audit</Button>
          <Button variant="outline" size="sm" onClick={downloadPDF} className="gap-1"><Download className="h-3 w-3" /> PDF</Button>
          {!isLocked && sow.status !== "sent_to_client" && (
            <Button size="sm" onClick={() => doAction("send", "SOW sent to client")} disabled={!!actionLoading} className="gap-1 bg-blue-600 text-white hover:bg-blue-700">
              {actionLoading === "send" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send to Client
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => doAction("duplicate", "SOW duplicated")} disabled={!!actionLoading} className="gap-1">
            <Copy className="h-3 w-3" /> Duplicate
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-5">
          {/* Client Details */}
          <Card className="border-border bg-card p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Client Details</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-muted-foreground">Legal Name</Label>
                <Input value={sow.client_legal_name} disabled={isLocked} onBlur={(e) => updateField("client_legal_name", e.target.value)} onChange={(e) => setSow({...sow, client_legal_name: e.target.value})} className="mt-1 h-9 border-border bg-background text-foreground" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Primary Contact</Label>
                <Input value={sow.client_primary_contact} disabled={isLocked} onBlur={(e) => updateField("client_primary_contact", e.target.value)} onChange={(e) => setSow({...sow, client_primary_contact: e.target.value})} className="mt-1 h-9 border-border bg-background text-foreground" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Email</Label>
                <Input value={sow.client_email} disabled={isLocked} onBlur={(e) => updateField("client_email", e.target.value)} onChange={(e) => setSow({...sow, client_email: e.target.value})} className="mt-1 h-9 border-border bg-background text-foreground" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Effective Date</Label>
                <Input type="date" value={sow.effective_date} disabled={isLocked} onBlur={(e) => updateField("effective_date", e.target.value)} onChange={(e) => setSow({...sow, effective_date: e.target.value})} className="mt-1 h-9 border-border bg-background text-foreground" />
              </div>
            </div>
          </Card>

          {/* Workflows */}
          <WorkflowsSection sowId={sowId} workflows={sow.workflows || []} isLocked={isLocked} onRefresh={fetchSOW} includedCount={sow.included_workflow_count} additionalPrice={sow.additional_workflow_unit_price} />

          {/* Short-form Sections */}
          <Card className="border-border bg-card p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Short-form Scope of Services Sections</h3>
            {["engagement_summary", "what_is_included", "client_responsibilities", "scope_control"].map((key) => (
              <div key={key} className="mb-3">
                <Label className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, " ")}</Label>
                <Textarea
                  value={sow.short_form_sections?.[key] || ""}
                  disabled={isLocked}
                  onBlur={(e) => updateSection(key, e.target.value)}
                  onChange={(e) => setSow({...sow, short_form_sections: {...sow.short_form_sections, [key]: e.target.value}})}
                  rows={3}
                  className="mt-1 border-border bg-background text-foreground text-sm"
                />
              </div>
            ))}
          </Card>
        </div>

        {/* Right sidebar */}
        <div className="space-y-5">
          {/* Commercial Summary */}
          <Card className="border-border bg-card p-5" data-testid="sow-commercial-summary">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Commercial Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Base Fee ({sow.included_workflow_count} workflows)</span><span className="font-semibold text-foreground">${sow.base_fee?.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Additional ({sow.additional_workflow_count || 0} workflows)</span><span className="text-foreground">${((sow.additional_workflow_count || 0) * (sow.additional_workflow_unit_price || 0)).toLocaleString()}</span></div>
              <Separator className="bg-border" />
              <div className="flex justify-between"><span className="font-semibold text-foreground">Estimated Total</span><span className="text-lg font-bold text-[var(--brand)]">${total.toLocaleString()}</span></div>
              <Separator className="bg-border" />
              <div className="flex justify-between text-xs"><span className="text-muted-foreground">Onsite Rate</span><span className="text-foreground">${sow.onsite_hourly_rate}/hr</span></div>
              <div className="flex justify-between text-xs"><span className="text-muted-foreground">Payment</span><span className="text-foreground">{sow.payment_terms}</span></div>
            </div>
          </Card>

          {/* Status Control */}
          {!isLocked && (
            <Card className="border-border bg-card p-5">
              <h3 className="mb-3 text-sm font-semibold text-foreground">Status</h3>
              <Select value={sow.status} onValueChange={(v) => updateField("status", v)}>
                <SelectTrigger className="border-border bg-background text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-border bg-popover text-popover-foreground">
                  {["draft", "internal_review", "sent_to_client", "cancelled"].map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Card>
          )}

          {/* Signature Info */}
          {sow.signature && (
            <Card className="border-border bg-emerald-500/10 p-5" data-testid="sow-signature-info">
              <h3 className="mb-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400">Approved</h3>
              <div className="space-y-1 text-xs text-foreground">
                <p><strong>Signed by:</strong> {sow.signature.signer_full_name}</p>
                <p><strong>Title:</strong> {sow.signature.signer_role}</p>
                <p><strong>Email:</strong> {sow.signature.signer_email}</p>
                <p><strong>Date:</strong> {new Date(sow.signature.signed_at).toLocaleString()}</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Audit Log Dialog */}
      {showAudit && (
        <Dialog open={showAudit} onOpenChange={setShowAudit}>
          <DialogContent className="border-border bg-card text-foreground max-w-lg max-h-[70vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Audit Log</DialogTitle></DialogHeader>
            <div className="space-y-2">
              {audit.map((a) => (
                <div key={a.id} className="border-b border-border pb-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-mono text-xs text-[var(--brand)]">{a.action}</span>
                    <span className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">by {a.action_by}{a.action_note ? ` — ${a.action_note}` : ""}</p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Workflows Section ───────────────────────────────────────

function WorkflowsSection({ sowId, workflows, isLocked, onRefresh, includedCount, additionalPrice }) {
  const [adding, setAdding] = useState(false);
  const [newWf, setNewWf] = useState({ workflow_name: "", business_problem: "", objective: "", deliverables: "", success_metric: "", assumptions: "" });

  const addWorkflow = async () => {
    setAdding(true);
    try {
      await fetch(`${API}/api/sow/admin/instances/${sowId}/workflows`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newWf),
      });
      setNewWf({ workflow_name: "", business_problem: "", objective: "", deliverables: "", success_metric: "", assumptions: "" });
      toast.success("Workflow added");
      onRefresh();
    } catch {
      toast.error("Failed to add workflow");
    } finally {
      setAdding(false);
    }
  };

  const deleteWorkflow = async (wfId) => {
    await fetch(`${API}/api/sow/admin/workflows/${wfId}`, { method: "DELETE" });
    toast.success("Workflow removed");
    onRefresh();
  };

  return (
    <Card className="border-border bg-card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Workflows ({workflows.length})</h3>
        {!isLocked && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1"><Plus className="h-3 w-3" /> Add</Button>
            </DialogTrigger>
            <DialogContent className="border-border bg-card text-foreground">
              <DialogHeader><DialogTitle>Add Workflow</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Workflow Name" value={newWf.workflow_name} onChange={(e) => setNewWf({...newWf, workflow_name: e.target.value})} className="border-border bg-background text-foreground" />
                <Textarea placeholder="Business Problem" value={newWf.business_problem} onChange={(e) => setNewWf({...newWf, business_problem: e.target.value})} rows={2} className="border-border bg-background text-foreground" />
                <Textarea placeholder="Objective" value={newWf.objective} onChange={(e) => setNewWf({...newWf, objective: e.target.value})} rows={2} className="border-border bg-background text-foreground" />
                <Textarea placeholder="Deliverables" value={newWf.deliverables} onChange={(e) => setNewWf({...newWf, deliverables: e.target.value})} rows={2} className="border-border bg-background text-foreground" />
                <Textarea placeholder="Success Metric" value={newWf.success_metric} onChange={(e) => setNewWf({...newWf, success_metric: e.target.value})} rows={2} className="border-border bg-background text-foreground" />
                <Button onClick={addWorkflow} disabled={adding || !newWf.workflow_name} className="w-full bg-[var(--brand)] text-white">
                  {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Workflow"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="space-y-3">
        {workflows.map((wf, i) => (
          <div key={wf.id} className="rounded-lg border border-border bg-background/50 p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">{i + 1}. {wf.workflow_name}</span>
                  <Badge variant="outline" className={`text-[10px] ${wf.workflow_type === "included" ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-[#007a9e] dark:text-amber-400"}`}>
                    {wf.workflow_type === "included" ? "INCLUDED" : `ADDITIONAL +$${wf.estimated_price?.toLocaleString()}`}
                  </Badge>
                </div>
              </div>
              {!isLocked && (
                <Button variant="ghost" size="icon" onClick={() => deleteWorkflow(wf.id)} className="h-7 w-7">
                  <Trash2 className="h-3 w-3 text-muted-foreground" />
                </Button>
              )}
            </div>
            <div className="mt-2 grid gap-1 text-xs text-muted-foreground">
              {wf.business_problem && <p><strong>Problem:</strong> {wf.business_problem}</p>}
              {wf.objective && <p><strong>Objective:</strong> {wf.objective}</p>}
              {wf.deliverables && <p><strong>Deliverables:</strong> {wf.deliverables}</p>}
              {wf.success_metric && <p><strong>Success Metric:</strong> {wf.success_metric}</p>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Main Component ──────────────────────────────────────────

export default function SOWAdmin({ session }) {
  const [sows, setSows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const fetchSows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sow/admin/instances`);
      setSows(await res.json());
    } catch {
      toast.error("Failed to load SOWs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSows(); }, [fetchSows]);

  if (selectedId) {
    return (
      <div className="admin-clarity">
        <SOWDetail sowId={selectedId} onBack={() => { setSelectedId(null); fetchSows(); }} />
      </div>
    );
  }

  return (
    <div className="py-10 admin-clarity">
      <SOWList
        sows={sows}
        loading={loading}
        onSelect={(s) => setSelectedId(s.id)}
        onCreate={(s) => { fetchSows(); setSelectedId(s.id); }}
        onRefresh={fetchSows}
      />
    </div>
  );
}
